import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import LineChartCard from "components/Charts/LineChartCard";
import { getWhatIf } from "services/experimentApi";
import { applyMethodToRows, calibrate } from "utils/calibrationCampaign";
import { withTruthRows } from "utils/groundTruth";

const signed = (value) => (value === null || value === undefined ? "—" : `${value > 0 ? "+" : ""}${value}`);

const EXCLUDE_TEXT = {
  no_truth: "no confident photo",
  truth_disagree: "photos disagree",
  few_readings: "too few sensor readings",
  sensor_moving: "sensor not steady",
  offset_changed: "pipe opening or radar zero changed",
  water_moving: "water moving",
  no_sensor_reading: "no sensor reading within 60 s",
  unread: "photo not read yet",
};

function Score({ score }) {
  if (!score) return <span className="text-slate-400">—</span>;
  return (
    <span className={score.low_n ? "text-slate-500" : ""}>
      {score.rmse} / {score.p95_abs} / {signed(score.bias)} <span className="text-slate-400">({score.n})</span>
    </span>
  );
}

const SensorTruthScatter = memo(function SensorTruthScatter({ pairs, method, reference }) {
  const raw = pairs.map((p) => ({ truth: p.truth_cm, sensor: p.sensor_cm }));
  const corrected = method && method.coefficients
    ? pairs
        .map((p) => ({ truth: p.truth_cm, sensor: calibrate(method.id, method.coefficients, p.sensor_cm, p.node_t, p.node_rh, reference) }))
        .filter((p) => typeof p.sensor === "number")
    : [];
  const values = [...raw, ...corrected].flatMap((p) => [p.truth, p.sensor]).filter((v) => typeof v === "number");
  if (!values.length) return null;
  const lo = Math.floor(Math.min(...values) - 1);
  const hi = Math.ceil(Math.max(...values) + 1);
  return (
    <div className="h-64" data-testid="sensor-truth-scatter">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 8, right: 8, bottom: 16, left: -8 }}>
          <CartesianGrid stroke="#f1f5f9" />
          <XAxis type="number" dataKey="truth" name="True level" unit=" cm" domain={[lo, hi]} tick={{ fontSize: 11 }} label={{ value: "true level (photo)", position: "insideBottom", offset: -8, fontSize: 11 }} />
          <YAxis type="number" dataKey="sensor" name="Sensor" unit=" cm" domain={[lo, hi]} tick={{ fontSize: 11 }} />
          <ReferenceLine segment={[{ x: lo, y: lo }, { x: hi, y: hi }]} stroke="#94a3b8" strokeDasharray="4 3" />
          <Tooltip cursor={{ strokeDasharray: "3 3" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Scatter name="Sensor as measured" data={raw} fill="#2563eb" isAnimationActive={false} />
          {corrected.length > 0 && <Scatter name={`Corrected (${method.label})`} data={corrected} fill="#16a34a" isAnimationActive={false} />}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
});

/**
 * The calibration what-if for a campaign or a single run, computed by the backend (padihat/experiment): every
 * correction method fitted on this experiment's pairs and scored on pairs it never saw - first held out from this
 * experiment, then on ALL earlier data of this node that has a true level (same Base-to-Ground). The best is marked;
 * any method can be picked to see its corrected line, and staff can apply it as the node's Calibration offset.
 *
 * Props: source ({campaign} | {run, controller, device}), rows (the chart rows), images (the run's photos, for the
 * truth line), onOpenImage(imageId), refreshKey (bump after a photo was labelled).
 */
const LEVELS = [
  { id: "est", label: "Estimated level", hint: "our range estimate (d1_est)" },
  { id: "cal", label: "Node's own level", hint: "the node's own distance" },
];

/**
 * Which of the node's two levels is fitted and scored. Both come from the same readings and photos; the backend picks
 * our estimate when it has enough usable pairs. A level with no usable pairs cannot be picked.
 */
function LevelChoice({ source, onPick }) {
  const options = source?.series_options;
  if (!options) return null;
  const shown = source.series;
  const startedOn = source.campaign_series;
  return (
    <div className="flex flex-col gap-1 text-xs" data-testid="whatif-level">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-600">Level</span>
        <div role="radiogroup" aria-label="Level to calibrate" className="flex overflow-hidden rounded-lg border border-slate-200">
          {LEVELS.map((level) => {
            const usable = options[level.id]?.usable ?? 0;
            const active = shown === level.id;
            return (
              <button
                key={level.id}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={!usable && !active}
                title={level.hint}
                onClick={() => !active && onPick(level.id)}
                className={`px-2.5 py-1 font-semibold ${active ? "bg-blue-600 text-white" : "bg-white text-slate-700 disabled:text-slate-300"}`}
              >
                {level.label} ({usable})
              </button>
            );
          })}
        </div>
      </div>
      <p className="text-slate-500" data-testid="whatif-level-note">
        Calibrating the <strong>{shown === "est" ? "estimated level" : "node's own level"}</strong>
        {shown === "est"
          ? ": our range estimate, the default whenever it has enough pairs. Compare the scores with the node's own level above."
          : (options.est?.with_sensor ?? options.est?.usable ?? 0) === 0
            ? ": this node sent no range estimate during this experiment."
            : !options.est.usable && !options.cal?.usable
              ? ": no pair has a true level yet, so neither level can be fitted."
              : `: the estimate has too few usable pairs here (${options.est.usable}).`}
        {startedOn && startedOn !== shown && shown === "est" &&
          " The campaign started on the node's own level because the node was not sending its estimate then; the estimate is used wherever it was sent."}
        {" "}Apply sets the offset for the level shown.
      </p>
    </div>
  );
}

function CalibrationWhatIf({ source, rows = [], images = [], onOpenImage, refreshKey = 0 }) {
  const [trainCount, setTrainCount] = useState(3);
  const [split, setSplit] = useState("first");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pickedId, setPickedId] = useState("");
  const [series, setSeries] = useState(""); // "" = the backend's choice (our estimate when it has enough pairs)
  const isRun = Boolean(source && !source.campaign);
  const sourceKey = source ? JSON.stringify(source) : "";

  const load = useCallback(async () => {
    if (!source) return;
    setLoading(true);
    setError("");
    try {
      setResult(await getWhatIf(source, { ...(isRun ? { trainCount, split } : {}), ...(series ? { series } : {}) }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
    // sourceKey stands for the source's contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, isRun, trainCount, split, series]);
  useEffect(() => {
    load();
  }, [load, refreshKey]);
  useEffect(() => {
    setPickedId("");
    setSeries("");
  }, [sourceKey]);

  const methods = result?.methods || [];
  const best = methods.find((m) => m.id === result?.best_id) || null;
  const picked = methods.find((m) => m.id === pickedId && m.coefficients) || null;
  const chosen = picked || best;
  const field = result?.source?.series === "est" ? "water_level_est_cm" : "water_level_cm";
  const reference = result?.reference;
  const usablePairs = useMemo(() => (result?.pairs || []).filter((p) => !p.excluded), [result]);

  const chartRows = useMemo(
    () => withTruthRows(chosen && chosen.id !== "none" ? applyMethodToRows(rows, chosen, field, reference) : rows, images),
    [rows, images, chosen, field, reference]
  );
  const showCorrected = Boolean(chosen && chosen.id !== "none" && chosen.coefficients);
  const lines = useMemo(
    () => [
      { dataKey: field, name: "Sensor as measured", color: "#2563eb", dot: true },
      ...(showCorrected ? [{ dataKey: "water_level_whatif_cm", name: `Corrected (${chosen.label})`, color: "#16a34a", dot: true }] : []),
      { dataKey: "truth_level_cm", name: "True level - from photos", color: "#dc2626", dot: true },
    ],
    [field, showCorrected, chosen?.label] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const excluded = Object.entries(result?.excluded || {});
  const history = result?.history || {};
  const reviewIds = result?.needs_review_photo_ids || [];
  if (!source) return null;
  return (
    <section className="col-span-1 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 lg:col-span-2" data-testid="calibration-whatif">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-800">
            Calibration what-if {source.campaign ? "· whole campaign" : `· run ${source.run}`}
          </h3>
          <p className="mt-1 max-w-3xl text-xs text-slate-500">
            Each correction is fitted on this experiment and scored only on pairs it was not fitted on:
            {source.campaign ? " leaving each condition out in turn" : " the first pairs fit, the rest score"}, and then on every earlier
            pair of this node with a true level. Numbers are RMSE / P95 / bias in cm (n). Error = sensor − truth: positive reads high.
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {isRun && (
        <div className="flex flex-wrap items-end gap-3 text-xs font-semibold text-slate-600">
          <label>
            Fit on
            <select aria-label="Training split" value={split} onChange={(e) => setSplit(e.target.value)} className="ml-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs">
              <option value="first">the first pairs</option>
              <option value="alternate">every other pair</option>
            </select>
          </label>
          {split === "first" && (
            <label>
              How many
              <input type="number" min="1" aria-label="Training pairs" value={trainCount} onChange={(e) => setTrainCount(Math.max(1, Number(e.target.value) || 3))} className="ml-2 w-16 rounded-lg border border-slate-200 px-2 py-1 text-xs" />
            </label>
          )}
        </div>
      )}

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {result && (
        <>
          <LevelChoice source={result.source} onPick={(value) => { setSeries(value); setPickedId(""); }} />
          <p className="text-xs text-slate-600">
            <strong>{result.fit_pairs}</strong> usable pair{result.fit_pairs === 1 ? "" : "s"} in this experiment
            {excluded.length > 0 && ` (left out: ${excluded.map(([k, n]) => `${n} ${EXCLUDE_TEXT[k] || k}`).join(", ")})`};{" "}
            <strong>{history.pairs ?? 0}</strong> earlier pair{history.pairs === 1 ? "" : "s"} of this node to check against
            {history.other_mounting?.length > 0 && ` (not counted: ${history.other_mounting.map((g) => `${g.pairs} with another mounting (offset ${g.offset_cm} cm)`).join(", ")})`}.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs" data-testid="whatif-methods">
              <thead className="text-slate-500">
                <tr>
                  <th className="py-1 pr-2" />
                  <th className="py-1 pr-2">Method</th>
                  <th className="py-1 pr-2">Correction</th>
                  <th className="py-1 pr-2">This experiment (held out)</th>
                  <th className="py-1">All earlier data of this node</th>
                </tr>
              </thead>
              <tbody>
                {methods.map((m) => (
                  <tr key={m.id} className={`border-t border-slate-100 ${chosen?.id === m.id ? "bg-emerald-50" : ""}`}>
                    <td className="py-1 pr-2">
                      <input
                        type="radio"
                        name="whatif-method"
                        aria-label={`Show ${m.label}`}
                        disabled={!m.coefficients}
                        checked={chosen?.id === m.id}
                        onChange={() => setPickedId(m.id)}
                      />
                    </td>
                    <td className="py-1 pr-2 font-semibold text-slate-800">
                      {m.label}
                      {m.id === result.best_id && <span className="ml-1 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-white">Best</span>}
                    </td>
                    <td className="py-1 pr-2 text-slate-600">{m.reason || m.text}</td>
                    <td className="py-1 pr-2"><Score score={m.held_out} /></td>
                    <td className="py-1"><Score score={m.history} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {best && (
            <p className="text-xs text-emerald-800" data-testid="whatif-best">
              Best: <strong>{best.label}</strong> ({best.text}), the lowest RMSE{" "}
              {result.best_basis === "history" ? `on ${history.pairs} earlier pairs of this node` : "held out on this experiment (too little earlier data to judge on)"}.
              {picked && picked.id !== best.id && ` Showing ${picked.label} because you picked it.`}
            </p>
          )}

          <LineChartCard title="Water level: as measured, corrected, and true" historicalData={chartRows} lines={lines} connectNulls colSpan="col-span-1" />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Sensor against the true level</p>
              <SensorTruthScatter pairs={usablePairs} method={chosen && chosen.id !== "none" ? chosen : null} reference={reference} />
              <p className="text-[11px] text-slate-500">On the dashed line the sensor is exactly right.</p>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">The best method on each earlier run</p>
              {history.breakdown?.runs?.length ? (
                <table className="mt-1 w-full text-left text-xs">
                  <thead className="text-slate-500">
                    <tr><th className="py-1 pr-2">Run</th><th className="py-1 pr-2">When</th><th className="py-1">RMSE / P95 / bias (n)</th></tr>
                  </thead>
                  <tbody>
                    {history.breakdown.runs.map((r) => (
                      <tr key={r.run_id} className="border-t border-slate-100">
                        <td className="py-1 pr-2 font-mono">{r.run_id}</td>
                        <td className="py-1 pr-2">{r.from ? new Date(r.from).toLocaleDateString() : ""}</td>
                        <td className="py-1"><Score score={r.score} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="mt-1 text-xs text-slate-400">No earlier data with a true level for this node yet.</p>
              )}
            </div>
          </div>

          {reviewIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              {reviewIds.length} photo{reviewIds.length === 1 ? "" : "s"} the reader was unsure about: a label from you turns
              each into a pair.
              {onOpenImage && (
                <button type="button" onClick={() => onOpenImage(reviewIds[0])} className="font-bold underline">Label the next one</button>
              )}
            </div>
          )}

          <p className="text-xs text-slate-500">
            Read-only: this compares methods on the data. A campaign's radar zero is applied from its result card above.
          </p>
        </>
      )}
      {!result && loading && <p className="text-sm text-slate-400">Fitting…</p>}
    </section>
  );
}

export default memo(CalibrationWhatIf);
